import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
} from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import RefreshIcon from "@mui/icons-material/Refresh";
import StopIcon from "@mui/icons-material/Stop";
import ClearIcon from "@mui/icons-material/Clear";
import { useAuthApi } from "../hooks/useAuthApi";
import { MGMT, OCPP } from "../env";
import { calcKwh, fmtDate, statusColor } from "../format";
import type { DriverIdTagInfo, Transaction, TransactionDataIssue } from "../types";
import { useToast } from "../toast";

const ACTIVE_STATUSES = new Set(["Active", "Charging", "Preparing", "SuspendedEV", "SuspendedEVSE"]);

const ISSUE_LABELS: Record<TransactionDataIssue, string> = {
  session_running_too_long: "Running > 2 days",
  negative_energy: "Negative energy",
  missing_meter_data: "Missing meter data",
  high_energy: "Energy > 300 kWh",
  no_start_time: "No start time",
};

function DataIssueChips({ issues }: { issues?: TransactionDataIssue[] }) {
  if (!issues || issues.length === 0) return null;
  return (
    <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
      {issues.map((issue) => (
        <Tooltip key={issue} title={ISSUE_LABELS[issue] ?? issue}>
          <Chip
            icon={<WarningAmberIcon />}
            label={ISSUE_LABELS[issue] ?? issue}
            color="warning"
            size="small"
          />
        </Tooltip>
      ))}
    </Box>
  );
}

export default function Transactions() {
  const api = useAuthApi();
  const [items, setItems] = useState<Transaction[]>([]);
  const [driverMap, setDriverMap] = useState<Record<string, DriverIdTagInfo>>({});
  const [loading, setLoading] = useState(false);
  const [stopping, setStopping] = useState<number | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [chargerFilter, setChargerFilter] = useState<string>("");
  const [search, setSearch] = useState<string>("");
  const toast = useToast();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [tx, dm] = await Promise.all([
        api<Transaction[]>(`${OCPP}/transactions`),
        api<Record<string, DriverIdTagInfo>>(`${MGMT}/drivers/id-tag-map`).catch(() => null),
      ]);
      setItems(tx ?? []);
      setDriverMap(dm ?? {});
    } catch (e) {
      toast.show((e as Error).message, "error");
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    load();
  }, [load]);

  const uniqueChargers = useMemo(() => {
    const seen = new Set<string>();
    return items
      .filter((t) => t.charge_point_id && !seen.has(t.charge_point_id) && seen.add(t.charge_point_id!))
      .map((t) => t.charge_point_id!);
  }, [items]);

  const uniqueStatuses = useMemo(() => {
    return [...new Set(items.map((t) => t.status))].sort();
  }, [items]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((t) => {
      if (statusFilter && t.status !== statusFilter) return false;
      if (chargerFilter && t.charge_point_id !== chargerFilter) return false;
      if (q) {
        const driver = t.id_tag ? driverMap[t.id_tag] : undefined;
        const haystack = [
          t.id_tag,
          t.charge_point_id,
          driver?.name,
          driver?.email,
          String(t.id),
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      return true;
    });
  }, [items, statusFilter, chargerFilter, search, driverMap]);

  const handleStop = async (t: Transaction) => {
    if (!window.confirm(`Stop transaction #${t.id}?`)) return;
    setStopping(t.id);
    try {
      await api(`${OCPP}/transactions/${t.id}/stop`, { method: "POST" });
      toast.show("Stop command sent", "success");
      load();
    } catch (e) {
      toast.show((e as Error).message, "error");
    } finally {
      setStopping(null);
    }
  };

  const clearFilters = () => {
    setStatusFilter("");
    setChargerFilter("");
    setSearch("");
  };

  const hasFilters = statusFilter || chargerFilter || search;

  return (
    <>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ mb: 2 }} alignItems="center">
        <TextField
          size="small"
          placeholder="Search by charger, driver, ID tag…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 260 }}
          InputProps={{
            endAdornment: search ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearch("")}>
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Status</InputLabel>
          <Select value={statusFilter} label="Status" onChange={(e) => setStatusFilter(e.target.value)}>
            <MenuItem value="">All</MenuItem>
            {uniqueStatuses.map((s) => (
              <MenuItem key={s} value={s}>{s}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Charger</InputLabel>
          <Select value={chargerFilter} label="Charger" onChange={(e) => setChargerFilter(e.target.value)}>
            <MenuItem value="">All</MenuItem>
            {uniqueChargers.map((c) => (
              <MenuItem key={c} value={c}>{c}</MenuItem>
            ))}
          </Select>
        </FormControl>
        {hasFilters && (
          <Button size="small" onClick={clearFilters}>Clear filters</Button>
        )}
        <Box sx={{ flex: 1 }} />
        <Button startIcon={<RefreshIcon />} onClick={load} disabled={loading}>
          Refresh
        </Button>
      </Stack>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>#</TableCell>
              <TableCell>Charger</TableCell>
              <TableCell>Connector</TableCell>
              <TableCell>Driver</TableCell>
              <TableCell>ID Tag</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Start</TableCell>
              <TableCell>Stop</TableCell>
              <TableCell align="right">Energy (kWh)</TableCell>
              <TableCell>Data Issues</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={11} align="center">
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            )}
            {!loading && filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={11} align="center">
                  {hasFilters ? "No transactions match the current filters." : "No transactions yet."}
                </TableCell>
              </TableRow>
            )}
            {filtered.map((t) => {
              const driver = t.id_tag ? driverMap[t.id_tag] : undefined;
              const isActive = ACTIVE_STATUSES.has(t.status);
              return (
                <TableRow key={t.id} hover sx={t.data_issues?.length ? { bgcolor: "warning.50" } : undefined}>
                  <TableCell>{t.id}</TableCell>
                  <TableCell sx={{ fontFamily: "monospace" }}>
                    {t.charge_point_id ?? t.charger_id}
                  </TableCell>
                  <TableCell>{t.connector_id ?? "–"}</TableCell>
                  <TableCell>
                    {driver ? (
                      <Tooltip title={driver.email}>
                        <span>{driver.name}</span>
                      </Tooltip>
                    ) : "–"}
                  </TableCell>
                  <TableCell sx={{ fontFamily: "monospace", fontSize: "0.8rem" }}>
                    {t.id_tag ?? "–"}
                  </TableCell>
                  <TableCell>
                    <Chip label={t.status} color={statusColor(t.status)} size="small" />
                  </TableCell>
                  <TableCell>{fmtDate(t.start_time)}</TableCell>
                  <TableCell>{fmtDate(t.stop_time)}</TableCell>
                  <TableCell align="right">
                    {calcKwh(t.meter_start, t.meter_stop)}
                  </TableCell>
                  <TableCell>
                    <DataIssueChips issues={t.data_issues} />
                  </TableCell>
                  <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                    {isActive && (
                      <Tooltip title="Stop transaction">
                        <span>
                          <IconButton
                            size="small"
                            color="error"
                            disabled={stopping === t.id}
                            onClick={() => handleStop(t)}
                          >
                            {stopping === t.id ? (
                              <CircularProgress size={16} color="inherit" />
                            ) : (
                              <StopIcon fontSize="small" />
                            )}
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
}
